import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Search, CheckCircle2, Circle, Truck, XCircle, RotateCcw, Wallet, Camera } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { inr, cx } from '@/lib/format';

const STEPS = ['confirmed', 'processing', 'shipped', 'out_for_delivery', 'delivered'];
const CUSTOMER_CANCELLABLE_STATUSES = ['pending', 'confirmed'];
const CUSTOMER_RETURN_ELIGIBLE_STATUSES = ['delivered'];
const MAX_RETURN_PHOTOS = 4;
const LABEL = { pending: 'Order Placed', confirmed: 'Order Confirmed', processing: 'Processing', shipped: 'Shipped', out_for_delivery: 'Out for Delivery', delivered: 'Delivered' };
const SIDE_STATES = {
  cancelled: { icon: XCircle, cls: 'border-red-200 bg-red-50 text-red-700', text: 'This order has been cancelled.' },
  return_requested: { icon: RotateCcw, cls: 'border-orange-200 bg-orange-50 text-orange-700', text: 'Return requested — we are reviewing your request.' },
  returned: { icon: RotateCcw, cls: 'border-orange-200 bg-orange-50 text-orange-800', text: 'This order has been returned.' },
  refund_pending: { icon: Wallet, cls: 'border-purple-200 bg-purple-50 text-purple-700', text: 'Refund is being processed.' },
  refunded: { icon: Wallet, cls: 'border-gray-200 bg-gray-50 text-gray-700', text: 'Refund completed.' },
};

export default function TrackOrder() {
  const { orderNumber } = useParams();
  const [num, setNum] = useState(orderNumber || '');
  const [order, setOrder] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedItems, setSelectedItems] = useState([]); // indices, only used when items.length > 1

  const [showReturn, setShowReturn] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returnImages, setReturnImages] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [submittingReturn, setSubmittingReturn] = useState(false);

  const track = async (e) => {
    e?.preventDefault();
    if (!num.trim()) return;
    try {
      const { data } = await api.get(`/orders/track/${num.trim()}`);
      setOrder(data);
      setConfirmCancel(false); setCancelReason(''); setSelectedItems([]);
      setShowReturn(false); setReturnReason(''); setReturnImages([]);
    }
    catch { toast.error('Order not found'); setOrder(null); }
  };
  React.useEffect(() => { if (orderNumber) track(); /* eslint-disable-next-line */ }, [orderNumber]);

  const activeItemIdxs = (order?.items || []).map((_, i) => i).filter((i) => !order.items[i].cancelled);
  const toggleItem = (i) => setSelectedItems((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]);

  const cancelOrder = async () => {
    if (!order) return;
    const multiItem = order.items.length > 1;
    if (multiItem && selectedItems.length === 0) { toast.error('Select at least one item to cancel'); return; }
    setCancelling(true);
    try {
      const { data } = multiItem
        ? await api.post(`/orders/track/${order.order_number}/cancel-items`, {
            item_indexes: selectedItems, reason: cancelReason.trim(),
          })
        : await api.post(`/orders/track/${order.order_number}/cancel`, { reason: cancelReason.trim() });
      setOrder(data);
      setConfirmCancel(false);
      setCancelReason('');
      setSelectedItems([]);
      toast.success(data.status === 'cancelled' ? 'Order cancelled' : 'Item(s) cancelled');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not cancel order');
    } finally {
      setCancelling(false);
    }
  };

  const onPickReturnPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX_RETURN_PHOTOS - returnImages.length);
    e.target.value = '';
    if (!files.length || !order) return;
    setUploadingPhoto(true);
    try {
      for (const file of files) {
        if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name} is over 8MB`); continue; }
        const form = new FormData();
        const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        form.append('upload_id', uploadId);
        form.append('chunk_index', '0');
        form.append('total_chunks', '1');
        form.append('filename', file.name);
        form.append('chunk', file);
        const { data } = await api.post(`/orders/track/${order.order_number}/return-request/photo`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data?.url) setReturnImages((prev) => [...prev, data.url]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };
  const removeReturnImage = (url) => setReturnImages((prev) => prev.filter((u) => u !== url));

  const submitReturn = async () => {
    if (!order) return;
    if (!returnReason.trim()) { toast.error('Please tell us the reason for the return'); return; }
    setSubmittingReturn(true);
    try {
      const { data } = await api.post(`/orders/track/${order.order_number}/return-request`, {
        reason: returnReason.trim(), images: returnImages,
      });
      setOrder(data);
      setShowReturn(false);
      setReturnReason('');
      setReturnImages([]);
      toast.success('Return requested');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit return request');
    } finally {
      setSubmittingReturn(false);
    }
  };

  // For side states, show progress up to the last main-flow status reached
  const lastMainIdx = order
    ? Math.max(STEPS.indexOf(order.status),
        ...(order.timeline || []).map((t) => STEPS.indexOf(t.status)))
    : -1;
  const side = order && SIDE_STATES[order.status];
  const ship = order?.shipping_info;

  return (
    <div className="container-gaze py-10">
      <div className="mx-auto max-w-2xl">
        <h1 className="text-3xl font-semibold">Track Your Order</h1>
        <form onSubmit={track} className="mt-4 flex gap-2">
          <div className="flex flex-1 items-center gap-2 rounded-full border border-border bg-white px-4"><Search size={16} className="text-muted-foreground" /><input value={num} onChange={(e) => setNum(e.target.value.toUpperCase())} placeholder="Enter order number (e.g. GZ...)" data-testid="track-input" className="w-full bg-transparent py-3 text-sm outline-none" /></div>
          <button type="submit" data-testid="track-button" className="rounded-full bg-[hsl(var(--primary))] px-6 text-sm font-semibold text-white">Track</button>
        </form>

        {order && (
          <div className="mt-8 rounded-3xl border border-border bg-white p-6" data-testid="track-order-card">
            <div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground">Order</p><p className="font-semibold">{order.order_number}</p></div><div className="text-right"><p className="text-xs text-muted-foreground">Total</p><p className="font-semibold">{inr(order.total)}</p></div></div>

            {CUSTOMER_CANCELLABLE_STATUSES.includes(order.status) && activeItemIdxs.length > 0 && (
              <div className="mt-4" data-testid="track-cancel-section">
                {!confirmCancel ? (
                  <button
                    type="button"
                    data-testid="cancel-order-button"
                    onClick={() => setConfirmCancel(true)}
                    className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 hover:bg-red-100"
                  >
                    <XCircle size={16} /> {order.items.length > 1 ? 'Cancel Item(s)' : 'Cancel Order'}
                  </button>
                ) : (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                    {order.items.length > 1 ? (
                      <>
                        <p className="text-sm font-medium text-red-700">Select the item(s) to cancel</p>
                        <div className="mt-2 space-y-2" data-testid="cancel-item-list">
                          {order.items.map((it, i) => it.cancelled ? (
                            <div key={i} className="flex items-center gap-2 opacity-50">
                              <img src={it.image} alt="" className="h-10 w-10 rounded-lg object-cover" />
                              <span className="flex-1 text-xs line-through">{it.name} × {it.qty}</span>
                              <span className="text-[10px] font-semibold uppercase text-muted-foreground">Cancelled</span>
                            </div>
                          ) : (
                            <label key={i} className="flex cursor-pointer items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedItems.includes(i)}
                                onChange={() => toggleItem(i)}
                                data-testid={`cancel-item-checkbox-${i}`}
                              />
                              <img src={it.image} alt="" className="h-10 w-10 rounded-lg object-cover" />
                              <span className="flex-1 text-xs">{it.name} × {it.qty}</span>
                              <span className="text-xs font-medium">{inr(it.line_total)}</span>
                            </label>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedItems(selectedItems.length === activeItemIdxs.length ? [] : activeItemIdxs)}
                          className="mt-2 text-xs font-semibold text-red-700 underline"
                        >
                          {selectedItems.length === activeItemIdxs.length ? 'Deselect all' : 'Select all (cancel entire order)'}
                        </button>
                      </>
                    ) : (
                      <p className="text-sm font-medium text-red-700">Cancel this order? This can't be undone.</p>
                    )}
                    <textarea
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Help us do better — why are you cancelling? (optional)"
                      rows={2}
                      className="mt-3 w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-xs outline-none"
                      data-testid="cancel-reason-input"
                    />
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        data-testid="cancel-order-confirm"
                        onClick={cancelOrder}
                        disabled={cancelling || (order.items.length > 1 && selectedItems.length === 0)}
                        className="rounded-full bg-red-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
                      >
                        {cancelling ? 'Cancelling…' : order.items.length > 1 ? `Cancel ${selectedItems.length || ''} item(s)` : 'Yes, cancel order'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setConfirmCancel(false); setCancelReason(''); setSelectedItems([]); }}
                        disabled={cancelling}
                        className="rounded-full border border-border px-4 py-2 text-xs font-semibold"
                      >
                        Keep order
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {CUSTOMER_RETURN_ELIGIBLE_STATUSES.includes(order.status) && (
              <div className="mt-4" data-testid="track-return-section">
                {!showReturn ? (
                  <button
                    type="button"
                    data-testid="request-return-button"
                    onClick={() => setShowReturn(true)}
                    className="flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-2.5 text-sm font-medium text-orange-700 hover:bg-orange-100"
                  >
                    <RotateCcw size={16} /> Request Return
                  </button>
                ) : (
                  <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
                    <p className="text-sm font-medium text-orange-700">Tell us what went wrong</p>
                    <textarea
                      value={returnReason}
                      onChange={(e) => setReturnReason(e.target.value)}
                      placeholder="Reason for return — help us know how we can do better"
                      rows={3}
                      className="mt-2 w-full rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm outline-none"
                      data-testid="return-reason-input"
                    />
                    <div className="mt-3">
                      <p className="text-xs font-medium text-orange-700">Photos (optional, up to {MAX_RETURN_PHOTOS})</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {returnImages.map((url) => (
                          <div key={url} className="relative h-16 w-16 overflow-hidden rounded-lg border border-border">
                            <img src={url} alt="" className="h-full w-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removeReturnImage(url)}
                              className="absolute right-0 top-0 rounded-bl bg-black/60 px-1 text-[10px] text-white"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        {returnImages.length < MAX_RETURN_PHOTOS && (
                          <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border border-dashed border-orange-300 text-orange-500">
                            {uploadingPhoto ? <span className="text-[10px]">…</span> : <Camera size={18} />}
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={onPickReturnPhotos}
                              className="hidden"
                              disabled={uploadingPhoto}
                              data-testid="return-photo-input"
                            />
                          </label>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        data-testid="return-submit"
                        onClick={submitReturn}
                        disabled={submittingReturn || uploadingPhoto}
                        className="rounded-full bg-orange-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
                      >
                        {submittingReturn ? 'Submitting…' : 'Submit return request'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowReturn(false); setReturnReason(''); setReturnImages([]); }}
                        disabled={submittingReturn}
                        className="rounded-full border border-border px-4 py-2 text-xs font-semibold"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {order.items?.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="track-items-list">
                {order.items.map((it, i) => (
                  <div key={i} className={cx('flex items-center gap-3', it.cancelled && 'opacity-50')}>
                    <img src={it.image} alt="" className="h-12 w-12 rounded-lg object-cover" />
                    <div className="flex-1">
                      <p className={cx('text-sm font-medium', it.cancelled && 'line-through')}>{it.name}</p>
                      <p className="text-xs text-muted-foreground">Qty {it.qty}{it.cancelled ? ' · Cancelled' : ''}</p>
                    </div>
                    <span className="text-sm font-semibold">{inr(it.line_total)}</span>
                  </div>
                ))}
              </div>
            )}

            {side && (
              <div className={cx('mt-4 flex items-center gap-2 rounded-xl border p-3 text-sm font-medium', side.cls)} data-testid="track-side-status">
                <side.icon size={18} /> {side.text}
              </div>
            )}

            <div className="mt-6 space-y-4">
              {STEPS.map((s, i) => (
                <div key={s} className="flex items-center gap-3">
                  {i <= lastMainIdx ? <CheckCircle2 size={22} className="text-green-600" /> : <Circle size={22} className="text-muted-foreground/40" />}
                  <span className={i <= lastMainIdx ? 'text-sm font-medium' : 'text-sm text-muted-foreground'}>{LABEL[s]}</span>
                </div>
              ))}
            </div>

            {ship && (ship.courier || ship.tracking_number || ship.estimated_delivery) && (
              <div className="mt-6 rounded-xl border border-border bg-[hsl(var(--gaze-ivory))] p-4" data-testid="track-shipping-info">
                <p className="flex items-center gap-1 text-xs font-semibold uppercase text-muted-foreground"><Truck size={13} /> Shipment details</p>
                <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
                  {ship.courier && <div><p className="text-[11px] text-muted-foreground">Courier</p><p className="font-medium">{ship.courier}</p></div>}
                  {ship.tracking_number && <div><p className="text-[11px] text-muted-foreground">Tracking / AWB</p><p className="font-medium">{ship.tracking_number}</p></div>}
                  {ship.shipped_at && <div><p className="text-[11px] text-muted-foreground">Shipped on</p><p className="font-medium">{ship.shipped_at}</p></div>}
                  {ship.estimated_delivery && <div><p className="text-[11px] text-muted-foreground">Estimated delivery</p><p className="font-medium">{ship.estimated_delivery}</p></div>}
                </div>
              </div>
            )}

            {(order.timeline || []).length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Updates</p>
                <ol className="mt-2 space-y-1.5" data-testid="track-timeline">
                  {[...order.timeline].reverse().map((e, i) => (
                    <li key={i} className="text-xs text-muted-foreground">
                      <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5 font-medium capitalize text-foreground">{(e.status || '').replace(/_/g, ' ')}</span>
                      <span className="ml-2">{new Date(e.at).toLocaleString()}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}